import { AfterViewInit, ChangeDetectorRef, Component, Input, QueryList, ViewChildren } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Subject, takeUntil } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { FormObjectListComponent } from '../../../../shared/forms/form-object-list.component';
import { FormObjectComponent } from '../../../../shared/forms/form-object.component';
import { FormValidation } from '../../../../shared/forms/form-validation';
import { UserCache } from '../../services/user/user.cache';
import { ComponentGroupService } from '../../services/form-service/component-group.service';
import { Estimate } from '../../models/estimates/estimate';
import { PartEstimate } from '../../models/estimates/part-estimate';
import { LaborEstimate } from '../../models/estimates/labor-estimate';
import { HazardousMaterialEstimate } from '../../models/estimates/hazardous-material-estimate';
import { EstimateTotal } from '../../models/estimates/estimate-total';
import { EstimateType } from '../../models/estimates/estimate-type';
import { EstimateComopnent } from '../estimate.component';
import { PartEstimateComponent } from '../part-estimate/part-estimate.component';
import { LaborEstimateComponent } from '../labor-estimate/labor-estimate.component';
import { MaterialEstimateComponent } from '../material-estimate/material-estimate.component';

@Component({
  selector: 'estimate-list',
  templateUrl: './estimate-list.component.html',
  styleUrl: './estimate-list.component.scss'
})
export class EstimateListComponent extends FormObjectListComponent<FormObjectComponent, Estimate> {
  private readonly _destroying$ = new Subject<void>();
  // Used to validate parent form before adding new estimate.
  @Input() form : UntypedFormGroup;
  @Input({ required: true }) componentGroupKey: number;
  @Input() isReadOnly: boolean = false;

  // This does not pick up the derived classes so broke them out separately.
  @ViewChildren(EstimateComopnent) override objectForms: QueryList<EstimateComopnent>;

  @ViewChildren(PartEstimateComponent) partForms: QueryList<PartEstimateComponent>;
  @ViewChildren(LaborEstimateComponent) laborForms: QueryList<LaborEstimateComponent>;
  @ViewChildren(MaterialEstimateComponent) materialForms: QueryList<MaterialEstimateComponent>;

  constructor(changeDetectorRef: ChangeDetectorRef,
              private componentGroupService: ComponentGroupService,
              private userCache: UserCache) {
    super(changeDetectorRef);
  }

  public enableEstimateDragAndDrop: boolean = environment.enableEstimateDragAndDrop == "true";
  public isAddPartEnabled: boolean = false;
  public isAddLaborEnabled: boolean = false;
  public isAddMaterialEnabled: boolean = false;

  public ngOnInit(): void {
    this.componentGroupService.getSubject(this.componentGroupKey)
        .pipe(takeUntil(this._destroying$))
        .subscribe(groupName => {
          const isComponentGroupSelected = groupName != null && groupName.length > 0;
          this.isAddPartEnabled = isComponentGroupSelected;
          this.isAddLaborEnabled = isComponentGroupSelected;
          this.isAddMaterialEnabled = isComponentGroupSelected;
        });
  }
	ngOnDestroy(): void {
		this._destroying$.next(undefined);
		this._destroying$.complete();
	}

  public isPart(type: EstimateType): boolean {
    return type == EstimateType.Part;
  }

  public isLabor(type: EstimateType): boolean {
    return type == EstimateType.Labor;
  }

  public isMaterial(type: EstimateType): boolean {
    return type == EstimateType.HazardousMaterial;
  }

  public get estimateTotal(): EstimateTotal {
    return new EstimateTotal(
      this.laborTotal,
      this.laborTaxPercentage,
      this.laborTaxTotal,
      this.partTotal,
      this.partTaxPercentage,
      this.partTaxTotal,
      this.materialTotal,
      this.materialTaxPercentage,
      this.materialTaxTotal
    );
  }

  public get laborTotal(): number {
    return this.laborForms?.reduce((sum, item) => sum + item.total, 0) ?? 0;
  }

  public get laborTaxPercentage(): number {
    return this.userCache?.selectedRepairFacility?.laborTaxRate ?? this.laborForms?.first?.laborRateTaxFormControl?.value ?? 0;
  }

  public get laborTaxTotal(): number {
    return this.laborForms?.reduce((sum, item) => sum + item.totalTax, 0) ?? 0;
  }

  public get partTotal(): number {
    return this.partForms?.reduce((sum, item) => sum + item.total, 0) ?? 0;
  }

  public get partTaxPercentage(): number {
    return this.userCache?.selectedRepairFacility?.partTaxRate ?? this.partForms?.first?.taxPercentage ?? 0;
  }

  public get partTaxTotal(): number {
    return this.partForms?.reduce((sum, item) => sum + item.totalTax, 0) ?? 0;
  }

  public get materialTotal(): number {
    return this.materialForms?.reduce((sum, item) => sum + item.total, 0) ?? 0;
  }

  public get materialTaxPercentage(): number {
    return this.userCache?.selectedRepairFacility?.partTaxRate ?? this.materialForms?.first?.taxPercentage ?? 0;  // Tax is same as parts.
  }

  public get materialTaxTotal(): number {
    return this.materialForms?.reduce((sum, item) => sum + item.totalTax, 0) ?? 0;
  }

  public override validateForm(): void {
    super.validateForm();

    // Check if the correction titles are filled.
    this.formCompleted = this.form.valid;

    this.partForms?.forEach(partForm => {
      if (!FormValidation.isValid(partForm.form)) this.formCompleted = false;
    });

    this.laborForms?.forEach(laborForm => {
      if (!FormValidation.isValid(laborForm.form)) this.formCompleted = false;
    });

    this.materialForms?.forEach(materialForms => {
      if (!FormValidation.isValid(materialForms.form)) this.formCompleted = false;
    });
  }

  public addPart(): void {
    this.objectList.push(new PartEstimate());
  }

  public addLabor(): void {
    this.objectList.push(new LaborEstimate());
  }

  public addMaterial(): void {
    this.objectList.push(new HazardousMaterialEstimate());
  }

  public drop(event: CdkDragDrop<Estimate[]>) {
    moveItemInArray(this.objectList, event.previousIndex, event.currentIndex);
  }
}
